import classNames from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { Button } from '../Button';
import { DropdownMenu } from '../DropdownMenu';
import { Icon } from '../Icon';
import { MenuItem } from '../MenuItem';
import type { SelectOption, SelectSize } from '../Select';
import { Tag } from '../Tag';
import { Typography } from '../Typography';

import styles from './MultiSelect.module.css';

export interface MultiSelectProps {
  options: SelectOption[];
  value?: string[];
  defaultValue?: string[];
  onChange?: (values: string[]) => void;
  label?: ReactNode;
  hideLabel?: boolean;
  hintText?: ReactNode;
  errorMessage?: ReactNode;
  destructive?: boolean;
  placeholder?: string;
  size?: SelectSize;
  required?: boolean;
  disabled?: boolean;
  /** Type-to-filter the option list (leading search icon + query input). */
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: ReactNode;
  /** Trailing keyboard hint in the input (e.g. `⌘K`). */
  shortcutHint?: ReactNode;
  /** Show the Reset / Select all footer. */
  showFooter?: boolean;
  resetLabel?: ReactNode;
  selectAllLabel?: ReactNode;
  /** Accessible name for a chip's dismiss button; receives the option label text. */
  removeLabel?: (label: string) => string;
  /**
   * Replace the selection chips with a compact summary of how many options are chosen. For a
   * trigger too narrow to hold chips — a cell in a filter row, say — where the count is what
   * matters rather than which items. Takes a function so the copy stays the app's to translate and
   * pluralise; the chips are used whenever it is absent.
   */
  summarizeSelection?: (count: number) => ReactNode;
  ariaLabel?: string;
  id?: string;
  className?: string;
}

const NONE = -1;

const optionText = (option: SelectOption): string =>
  option.searchValue ?? (typeof option.label === 'string' ? option.label : option.value);

const nextEnabled = (options: SelectOption[], from: number, dir: 1 | -1): number => {
  const { length } = options;
  if (length === 0) return NONE;
  for (let step = 1; step <= length; step += 1) {
    const index = (from + dir * step + length * length) % length;
    if (!options[index]?.disabled) return index;
  }
  return from;
};

/**
 * Multi-select combobox: an `Input`-styled trigger holding dismissable `Tag` chips for the
 * current selection plus a filter input, opening a multi-selectable `DropdownMenu` listbox of
 * checkbox `MenuItem`s. Selecting toggles an option and keeps the menu open; an optional
 * Reset / Select all footer bulk-edits the set. Follows the WAI-ARIA multi-select combobox
 * pattern (arrows / Home / End / Enter to toggle / Backspace to drop the last chip / Escape).
 *
 * @interim Seeded from the Figma "Select field" multi-select; replaceable by the UI Kit.
 */
export function MultiSelect({
  options,
  value,
  defaultValue,
  onChange,
  label,
  hideLabel = false,
  hintText,
  errorMessage,
  destructive = false,
  placeholder,
  size = 'md',
  required,
  disabled = false,
  searchable = false,
  searchPlaceholder,
  noResultsText,
  shortcutHint,
  showFooter = false,
  resetLabel,
  selectAllLabel,
  removeLabel,
  summarizeSelection,
  ariaLabel,
  id,
  className,
}: MultiSelectProps) {
  const messages = useUiMessages();
  const resolvedPlaceholder = placeholder ?? messages.multiSelect?.placeholder ?? 'Select…';
  const resolvedNoResultsText = noResultsText ?? messages.multiSelect?.noResults ?? 'No results';
  const resolvedResetLabel = resetLabel ?? messages.multiSelect?.reset ?? 'Reset';
  const resolvedSelectAllLabel = selectAllLabel ?? messages.multiSelect?.selectAll ?? 'Select all';
  const resolvedRemoveLabel =
    removeLabel ?? messages.multiSelect?.remove ?? ((text: string) => `Remove ${text}`);
  const autoId = useId();
  const rootId = id ?? autoId;
  const labelId = `${rootId}-label`;
  const listId = `${rootId}-list`;
  const messageId = `${rootId}-message`;
  const hasError = destructive || errorMessage != null;
  const message = errorMessage ?? hintText;

  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<string[]>(defaultValue ?? []);
  const selectedValues = isControlled ? value : internal;
  const selectedSet = new Set(selectedValues);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(NONE);
  const [query, setQuery] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const visibleOptions =
    searchable && query.trim() !== ''
      ? options.filter((o) => optionText(o).toLowerCase().includes(query.trim().toLowerCase()))
      : options;

  const commit = (next: string[]) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setActiveIndex(NONE);
    setQuery('');
  };

  const toggle = (option: SelectOption | undefined) => {
    if (!option || option.disabled) return;
    commit(
      selectedSet.has(option.value)
        ? selectedValues.filter((v) => v !== option.value)
        : [...selectedValues, option.value],
    );
    inputRef.current?.focus();
  };

  const removeValue = (value: string) => {
    commit(selectedValues.filter((v) => v !== value));
    inputRef.current?.focus();
  };

  const reset = () => {
    commit([]);
    inputRef.current?.focus();
  };

  const selectAll = () => {
    commit(options.filter((o) => !o.disabled).map((o) => o.value));
    inputRef.current?.focus();
  };

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const item = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${String(activeIndex)}"]`,
    );
    if (typeof item?.scrollIntoView === 'function') item.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) openMenu();
        setActiveIndex((i) => nextEnabled(visibleOptions, i, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) openMenu();
        setActiveIndex((i) => nextEnabled(visibleOptions, i, -1));
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(nextEnabled(visibleOptions, NONE, 1));
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(nextEnabled(visibleOptions, 0, -1));
        }
        break;
      case 'Enter':
        if (open && activeIndex >= 0) {
          event.preventDefault();
          toggle(visibleOptions[activeIndex]);
        } else if (!open) {
          event.preventDefault();
          openMenu();
        }
        break;
      case 'Backspace':
        if (query === '' && selectedValues.length > 0) {
          const last = selectedValues[selectedValues.length - 1];
          if (last != null) removeValue(last);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          closeMenu();
        }
        break;
      case 'Tab':
        if (open) closeMenu();
        break;
      default:
        break;
    }
  };

  const activeId = open && activeIndex >= 0 ? `${listId}-opt-${String(activeIndex)}` : undefined;

  const footer = showFooter ? (
    <>
      <Button
        hierarchy="link-gray"
        size="sm"
        onClick={reset}
        disabled={selectedValues.length === 0}
      >
        {resolvedResetLabel}
      </Button>
      <span className={styles.footerSpacer} />
      <Button hierarchy="link-color" size="sm" onClick={selectAll}>
        {resolvedSelectAllLabel}
      </Button>
    </>
  ) : undefined;

  return (
    <div className={classNames(styles.field, className)}>
      {label != null && (
        <span id={labelId} className={classNames(styles.label, hideLabel && styles.srOnly)}>
          <Typography as="span" variant="text-sm" weight="medium">
            {label}
          </Typography>
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </span>
      )}

      {/* Clicking anywhere in the field focuses the combobox input; the input owns the a11y role. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={classNames(
          styles.box,
          styles[size],
          hasError && styles.error,
          disabled && styles.disabled,
        )}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
      >
        {searchable && (
          <Icon name="search-lg" size={20} className={classNames(styles.searchIcon)} aria-hidden />
        )}
        {summarizeSelection != null
          ? selectedOptions.length > 0 && (
              <Typography as="span" variant="text-md" className={classNames(styles.summary)}>
                {summarizeSelection(selectedOptions.length)}
              </Typography>
            )
          : selectedOptions.map((option) => (
              <Tag
                key={option.value}
                size="sm"
                iconLeading={option.leading}
                removeLabel={resolvedRemoveLabel(optionText(option))}
                {...(disabled
                  ? {}
                  : {
                      onRemove: () => {
                        removeValue(option.value);
                      },
                    })}
              >
                {option.label}
              </Tag>
            ))}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          className={styles.input}
          value={query}
          placeholder={
            selectedOptions.length === 0 ? (searchPlaceholder ?? resolvedPlaceholder) : ''
          }
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={activeId}
          aria-labelledby={label != null ? labelId : undefined}
          aria-label={label == null ? ariaLabel : undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={message != null ? messageId : undefined}
          aria-required={required ? true : undefined}
          readOnly={!searchable}
          onFocus={openMenu}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
        {shortcutHint != null && selectedOptions.length === 0 && query === '' && (
          <kbd className={styles.shortcut} aria-hidden>
            {shortcutHint}
          </kbd>
        )}
        <Icon name="chevron-down" size={20} className={classNames(styles.chevron)} aria-hidden />
      </div>

      <DropdownMenu
        anchorRef={inputRef}
        open={open}
        onClose={closeMenu}
        matchWidth
        id={listId}
        listRef={listRef}
        footer={footer}
        aria-labelledby={label != null ? labelId : undefined}
        aria-label={label == null ? ariaLabel : undefined}
        aria-activedescendant={activeId}
        aria-multiselectable
      >
        {visibleOptions.length === 0 ? (
          <div className={styles.noResults}>
            <Typography as="p" variant="text-sm" className={classNames(styles.noResultsText)}>
              {resolvedNoResultsText}
            </Typography>
          </div>
        ) : (
          visibleOptions.map((option, index) => (
            <MenuItem
              key={option.value}
              id={`${listId}-opt-${String(index)}`}
              data-index={index}
              size={size}
              checkbox
              leading={option.leading}
              supportingText={option.supportingText}
              selected={selectedSet.has(option.value)}
              active={index === activeIndex}
              disabled={option.disabled ?? false}
              onSelect={() => {
                toggle(option);
              }}
              onMouseEnter={() => {
                if (!option.disabled) setActiveIndex(index);
              }}
            >
              {option.label}
            </MenuItem>
          ))
        )}
      </DropdownMenu>

      {message != null && (
        <Typography
          as="p"
          variant="text-sm"
          id={messageId}
          className={classNames(hasError ? styles.errorText : styles.hint)}
        >
          {message}
        </Typography>
      )}
    </div>
  );
}
